import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axiosInstance";
import { getVisitorId } from "../../utils/analytics";

const token = localStorage.getItem("userToken") || null;

export const googleLogin = createAsyncThunk(
  "auth/google",
  async (credential, { rejectWithValue }) => {
    try {
      const visitorId = getVisitorId();
      const response = await axiosInstance.post(`/auth/google`, { credential, visitorId });
      localStorage.setItem("userToken", response.data.token);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Google sign in failed");
    }
  }
);

export const emailLogin = createAsyncThunk(
  "auth/emailLogin",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const visitorId = getVisitorId();
      const response = await axiosInstance.post(`/auth/login`, { email, password, visitorId });
      localStorage.setItem("userToken", response.data.token);
      return response.data;
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.isEmailVerified === false) {
        return rejectWithValue({ message: err.response.data.message, email, unverified: true });
      }
      return rejectWithValue(err.response?.data?.message || "Login failed");
    }
  }
);

export const emailSignup = createAsyncThunk(
  "auth/emailSignup",
  async (userData, { rejectWithValue }) => {
    try {
      const visitorId = getVisitorId();
      const response = await axiosInstance.post(`/auth/signup`, { ...userData, visitorId });
      // We don't set token here because they need to verify
      return { ...response.data, email: userData.email }; 
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Signup failed");
    }
  }
);

export const verifyEmail = createAsyncThunk(
  "auth/verifyEmail",
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      const visitorId = getVisitorId();
      const response = await axiosInstance.post(`/auth/verify-email`, { email, otp, visitorId });
      if (response.data.token) {
        localStorage.setItem("userToken", response.data.token);
      }
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Verification failed");
    }
  }
);

export const resendOtp = createAsyncThunk(
  "auth/resendOtp",
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/auth/resend-otp`, { email });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to resend OTP");
    }
  }
);

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/auth/forgot-password`, { email });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to send reset email");
    }
  }
);

export const verifyResetOtp = createAsyncThunk(
  "auth/verifyResetOtp",
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/auth/verify-reset-otp`, { email, otp });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Invalid or expired OTP");
    }
  }
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async ({ email, otp, newPassword }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/auth/reset-password`, { email, otp, newPassword });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to reset password");
    }
  }
);

export const fetchCurrentUser = createAsyncThunk(
  "auth/fetchCurrentUser",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const currentToken = state.auth.token;
      
      if (!currentToken) return rejectWithValue("No token found");

      const response = await axiosInstance.get(`/auth/me`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });
      return response.data.user;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch user");
    }
  }
);

export const updateProfile = createAsyncThunk(
  "auth/updateProfile",
  async (profileData, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const currentToken = state.auth.token;
      
      const response = await axiosInstance.put(`/auth/update-profile`, profileData, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });
      return response.data.user;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to update profile");
    }
  }
);

const initialState = {
  user: null,
  token,
  isAuthenticated: !!token,
  loading: false,
  loadingProvider: null,
  error: null,
  isNewUser: false,
  pendingVerificationEmail: null,
  successMessage: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logoutUser: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
      state.pendingVerificationEmail = null;
      state.successMessage = null;
      localStorage.removeItem("userToken");
      localStorage.removeItem("visitor_id"); // Generate a new one next time
      sessionStorage.removeItem("session_id");
      sessionStorage.removeItem("last_activity");
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    setPendingVerificationEmail: (state, action) => {
      state.pendingVerificationEmail = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Google Login
      .addCase(googleLogin.pending, (state) => {
        state.loading = true;
        state.loadingProvider = 'google';
        state.error = null;
      })
      .addCase(googleLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingProvider = null;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.isNewUser = action.payload.isNewUser || false;
        if (action.payload.user.visitorId) {
          localStorage.setItem("visitor_id", action.payload.user.visitorId);
        }
      })
      .addCase(googleLogin.rejected, (state, action) => {
        state.loading = false;
        state.loadingProvider = null;
        state.error = action.payload;
      })

      // Email Login
      .addCase(emailLogin.pending, (state) => {
        state.loading = true;
        state.loadingProvider = 'email';
        state.error = null;
      })
      .addCase(emailLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingProvider = null;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.isNewUser = action.payload.isNewUser || false;
        if (action.payload.user.visitorId) {
          localStorage.setItem("visitor_id", action.payload.user.visitorId);
        }
      })
      .addCase(emailLogin.rejected, (state, action) => {
        state.loading = false;
        state.loadingProvider = null;
        if (action.payload?.unverified) {
           state.error = action.payload.message;
           state.pendingVerificationEmail = action.payload.email;
        } else {
           state.error = action.payload;
        }
      })

      // Email Signup
      .addCase(emailSignup.pending, (state) => {
        state.loading = true;
        state.loadingProvider = 'email';
        state.error = null;
      })
      .addCase(emailSignup.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingProvider = null;
        state.pendingVerificationEmail = action.payload.email;
        state.successMessage = action.payload.message;
      })
      .addCase(emailSignup.rejected, (state, action) => {
        state.loading = false;
        state.loadingProvider = null;
        state.error = action.payload;
      })

      // Verify Email
      .addCase(verifyEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.isNewUser = action.payload.isNewUser || false;
        state.pendingVerificationEmail = null;
        if (action.payload.user.visitorId) {
          localStorage.setItem("visitor_id", action.payload.user.visitorId);
        }
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Resend OTP
      .addCase(resendOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resendOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(resendOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Forgot Password
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Verify Reset OTP
      .addCase(verifyResetOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyResetOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(verifyResetOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Reset Password
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Current User
      .addCase(fetchCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
        if (action.payload.visitorId) {
          localStorage.setItem("visitor_id", action.payload.visitorId);
        }
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        // If the token is invalid/expired, we should log the user out
        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
        localStorage.removeItem("userToken");
        localStorage.removeItem("visitor_id");
        sessionStorage.removeItem("session_id");
        sessionStorage.removeItem("last_activity");
      })

      // Update Profile
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { logoutUser, clearError, clearSuccessMessage, setPendingVerificationEmail } = authSlice.actions;
export default authSlice.reducer;
